import { message } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { Commodity } from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import { UseTranslateType } from '@/hooks/useTranslate';
import UserStore, { IDENTITYKEY } from '@/store/User';
import { AIFunction } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { EventNewID, GoogleEventID, StatusEnum } from '@/utils/eventlog/eventNewConfig';
import { trackEvent, trackGoogleEvent } from '@/utils/eventlog/logAdapter';

import { TabActiveKey, TabContentProps } from '..';
import { PayQRCodeShow } from '../components/qrcode';
import { getPlanTag } from '../utils';
import { channelPay, channelPolling } from './channelPay';
import { stripePolling } from './stripePay';

// 支付时保留一下对应的信息
export type OrderInfo = {
  orderId: string;
  orderType: TabActiveKey | '';
  orderCommodityCode: string;
};
interface UsePaymentProps {
  t: UseTranslateType | undefined;
}
interface HandlePayProps {
  data: Commodity[number];
  feature: AIFunction;
  btnIndex: number;
  price: string;
  currency: string;
}

function getRecommendCredit(singlePurchaseConfig: Commodity) {
  try {
    return singlePurchaseConfig.find((item) => item?.content?.label);
  } catch (error) {
    return false;
  }
}

const usePayment = ({ t }: UsePaymentProps) => {
  // 标识是否支付完成，轮训到支付成功后设置为true
  const [isPurchaseSuccess, setIsPurchaseSuccess] = useState(false);
  const featureRef = useRef<AIFunction>(AIFunction.Other);
  const [activeTab] = useState<TabContentProps['activeKey']>('once');
  // 单次购买包的高亮code
  const [highlightSingleCode, setHighlightSingleCode] = useState<string>('');
  const purchaseLimitLoading = UserStore((store) => store.purchaseLimitLoading);

  const { limitConfig, singlePurchaseConfig, withUpdate } = useContext(RightContext);

  const formatSingleData = singlePurchaseConfig.sort((a, b) => a.sort - b.sort);

  // activeTabTracking 用于埋点
  const activeTabTracking = useRef<TabContentProps['activeKey']>(activeTab);
  const activeComboTracking = useRef<IDENTITYKEY>('advancedVip');

  const [singleLoadingStates, setSingleLoadingStates] = useState<Array<boolean>>(
    formatSingleData.map(() => false),
  );

  const pollPayKey = 'pollPayKey';
  const orderInfo = useRef<OrderInfo>({
    orderId: '',
    orderType: '',
    orderCommodityCode: '',
  });
  let timer = useRef<NodeJS.Timeout | null>(null);
  // 用于销毁二维码弹窗
  let QRCodeDestroyHandler = useRef();

  const setSingleBtnLoading = (index: number, bool: boolean) => {
    const newLoadingStates = [...singleLoadingStates];
    newLoadingStates[index] = bool;
    setSingleLoadingStates(newLoadingStates);
  };
  const [, setModalParent] = useState<HTMLElement | null>(null);

  const channelPayCallback = {
    purchase: {
      success: (orderId: string) => {
        if (!isCnArea) {
          message.open({
            key: pollPayKey,
            type: 'loading',
            content: t && t('merror.sub.purchasing'),
            duration: 0,
          });
        }

        orderInfo.current.orderId = orderId;
        if (isCnArea) {
          // 支付发起成功，开始轮训
          channelPolling({
            orderInfo: orderInfo.current,
            t,
            pollPayKey: 'pollPayKey',
            timer,
            callback: channelPayCallback.polling,
          });
        }
      },
      error: (msg: string) => {
        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.FAIL,
        });
        message.open({
          key: pollPayKey,
          type: 'error',
          content: msg || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
      },
    },
    polling: {
      success: async () => {
        setIsPurchaseSuccess(true);
        message.open({
          key: pollPayKey,
          type: 'success',
          content: t && t('merror.sub.purchasesuc'),
          duration: 2,
        });
        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.SUCCESS,
        });
        if (isCnArea) {
          // @ts-ignore
          // 关闭二维码弹窗
          QRCodeDestroyHandler?.current?.();
        }
        await withUpdate(() => {}, false);
      },
      error: (msg: 'timeout' | string) => {
        if (msg === 'timeout') {
          trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
            source: featureRef.current,
            plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
            result: StatusEnum.FAIL,
          });
        }
        message.open({
          key: pollPayKey,
          type: 'error',
          content: t && t('merror.sub.longtime'),
          duration: 2,
        });
      },
    },
  };
  const stripePayCallback = {
    polling: {
      success: async () => {
        setIsPurchaseSuccess(true);
        message.open({
          key: pollPayKey,
          type: 'success',
          content: t && t('merror.sub.purchasesuc'),
          duration: 2,
        });
        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.SUCCESS,
        });
        await withUpdate(() => {}, false);
      },
      error(msg: 'timeout' | string) {
        if (msg === 'timeout') {
          trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
            source: featureRef.current,
            plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
            result: StatusEnum.FAIL,
          });
        }
      },
    },
  };
  const handlePay = async ({
    data,
    btnIndex,
    feature,
    price,
    currency,
  }: HandlePayProps) => {
    if (singleLoadingStates.some((state) => state)) return;
    setIsPurchaseSuccess(false);
    featureRef.current = feature;

    if (!isCnArea) {
      setSingleBtnLoading(btnIndex, true);
    }

    const plan = getPlanTag(activeComboTracking.current, activeTabTracking.current);

    trackGoogleEvent(GoogleEventID.BeginCheckout, {
      subscription_plan: plan,
    });

    trackEvent(EventNewID.SUBSCRIPTION_POP_CLICK, {
      source: feature,
      plan,
    });

    const commodityCode = data?.commodityCode as string;

    if (!commodityCode) {
      console.error('商品code不存在');
      return;
    }

    orderInfo.current = {
      orderId: '',
      orderType: 'once',
      orderCommodityCode: commodityCode,
    };
    try {
      if (isCnArea) {
        PayQRCodeShow({
          destroyHandler: QRCodeDestroyHandler,
          feature,
          commodityCode,
          price,
          currency,
          callback: channelPayCallback.purchase,
          onClose: () => {
            // 关闭轮训message提示
            message.destroy(pollPayKey);
            timer.current && clearTimeout(timer.current);
          },
          onTabChange(orderId) {
            // tab切换之后，更新orderId，重置轮训
            orderInfo.current.orderId = orderId;
            timer.current && clearTimeout(timer.current);
            channelPolling({
              orderInfo: orderInfo.current,
              t,
              pollPayKey: 'pollPayKey',
              timer,
              callback: channelPayCallback.polling,
            });
          },
        });
      } else {
        await channelPay({
          feature,
          commodityCode,
          callback: channelPayCallback.purchase,
        });
      }
    } catch (error) {
      message.open({
        key: pollPayKey,
        type: 'error',
        content: (error as any).message || (t && t('merror.sub.purchasefail')),
        duration: 2,
      });
    } finally {
      if (!isCnArea) {
        setSingleBtnLoading(btnIndex, false);
      }
    }
  };
  useEffect(() => {
    const modalElement = document.querySelector('.ai-credit-modal');
    if (modalElement && modalElement.parentElement) {
      setModalParent(modalElement.parentElement);
    }
  }, []);
  useEffect(() => {
    // 获取高亮的AI积分包
    const aicredit = getRecommendCredit(singlePurchaseConfig);
    if (aicredit) {
      setHighlightSingleCode(aicredit.commodityCode);
    }
  }, [limitConfig]);

  useEffect(() => {
    // 监听用户是否切换标签页，用于支付结果的弹窗提示
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // 用户回到此标签页
        try {
          // 防止没有发起订单就去轮训
          if (!orderInfo.current.orderId && !orderInfo.current.orderType) {
            return;
          }

          console.log('stripe轮训');
          await stripePolling({
            orderInfo: orderInfo.current,
            t,
            pollPayKey,
            timer,
            callback: stripePayCallback.polling,
          });

          await withUpdate(() => {}, false);
        } catch (error) {
          console.error(error);
        }
      } else {
        // 用户离开此标签页
        timer.current && clearTimeout(timer.current);
      }
    };
    if (!isCnArea) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // 清理函数，移除事件监听器
    return () => {
      timer.current && clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [limitConfig, singlePurchaseConfig]);

  return {
    handlePay,
    singleLoadingStates,
    activeTab,
    highlightSingleCode,
    formatSingleData,
    purchaseLimitLoading,
    isPurchaseSuccess,
  };
};
export default usePayment;
